import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import Popup from 'reactjs-popup';
import { Cropper } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { toastError } from './toastHelper';
import { proxiedImageUrl } from './cropUtils';

const JPEG_QUALITY = 0.95;

const popupStyle = {
  border: 'none',
  borderRadius: '8px',
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  width: '700px',
  padding: '0px',
  background: 'var(--sl-bg-primary)'
};

const ImageCropModal = ({
  open,
  onClose,
  onCropComplete,
  imageSrc,
  aspectRatio,
  fileName,
  initialCrop = null,
  onImageReplaced = null
}) => {
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);
  const [cropping, setCropping] = useState(false);
  const [loadedImageSrc, setImageDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open && imageSrc) {
      setError(null);
      if (imageSrc.startsWith('data:') || imageSrc.startsWith('blob:')) {
        setImageDataUrl(imageSrc);
        setLoading(false);
      } else {
        setLoading(true);
        const proxied = proxiedImageUrl(imageSrc);
        const src = reloadKey > 0
          ? `${proxied}${proxied.includes('?') ? '&' : '?'}_t=${Date.now()}`
          : proxied;
        loadImageAsDataUrl(src)
          .then((dataUrl) => {
            if (mountedRef.current) {
              setImageDataUrl(dataUrl);
              setLoading(false);
            }
          })
          .catch(() => {
            if (mountedRef.current) {
              setError('load');
              setLoading(false);
            }
          });
      }
    } else if (!open) {
      setImageDataUrl(null);
      setError(null);
      setReloadKey(0);
    }
  }, [open, imageSrc, reloadKey]);

  const loadImageAsDataUrl = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
          resolve(dataUrl);
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      // Whatever source and framing the caller was holding describes the image we
      // just replaced, so it has to hear about this first: otherwise a box measured
      // against this file gets stored against the previous file's source. Ahead of
      // the src update because FileReader callbacks are outside React 17's batching,
      // so the render carrying the new src would otherwise land while initialCrop
      // still holds the previous image's box.
      onImageReplaced && onImageReplaced(file);
      setImageDataUrl(reader.result);
      setError(null);
    };
    reader.onerror = () => setError('load');
    reader.readAsDataURL(file);
  };

  const canvasToBlob = (canvas, mimeType, quality) => {
    return new Promise((resolve) => {
      canvas.toBlob(resolve, mimeType, quality);
    });
  };

  // The framing the user chose, in the displayed image's pixel space (the cropper
  // has already applied EXIF orientation, so a consumer must orient before
  // applying this). Reported alongside the cropped file so the caller can store it
  // next to the uncropped source and derive other ratios later.
  const cropBox = (cropper) => {
    const coordinates = cropper.getCoordinates({ round: true });
    const image = cropper.getImage();
    if (!coordinates || !image || !image.width || !image.height) return null;

    // getImage() reports the UN-oriented natural size: for an EXIF-rotated file the
    // cropper resets the orientation tag in the buffer and carries the rotation in
    // transforms.rotate instead. getCoordinates() is in the oriented space (it is
    // clamped to rotateSize(imageSize, rotate)), so on a quarter turn the two are
    // transposed -- an iPhone portrait would report 4032x3024 for a box measured in
    // 3024x4032, which either fails the model's bounds check outright or persists a
    // box describing a source shape that never existed.
    const quarterTurn = Math.abs((image.transforms?.rotate || 0) % 180) === 90;

    return {
      left: coordinates.left,
      top: coordinates.top,
      width: coordinates.width,
      height: coordinates.height,
      source_width: quarterTurn ? image.height : image.width,
      source_height: quarterTurn ? image.width : image.height
    };
  };

  // Reopening the cropper on an image we already have framing for should show that
  // framing, not a fresh centered stencil.
  //
  // This has to go through the cropper's own `defaultCoordinates` initialization
  // setting. Calling setCoordinates() imperatively from onReady does not survive:
  // the cropper finishes initializing afterwards and normalizes the stencil back to
  // its default centered position, so the restore silently did nothing.
  const initialCoordinates = initialCrop
    ? {
      left: initialCrop.left,
      top: initialCrop.top,
      width: initialCrop.width,
      height: initialCrop.height
    }
    : null;

  const handleCropAndUpload = async () => {
    const cropper = cropperRef.current;
    if (!cropper) return;

    setCropping(true);
    try {
      const canvas = cropper.getCanvas();
      if (!canvas) {
        toastError('Failed to process image. Please try again.');
        if (mountedRef.current) setCropping(false);
        return;
      }

      const mimeType = fileName.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
      const blob = await canvasToBlob(canvas, mimeType, JPEG_QUALITY);

      if (!mountedRef.current) return;

      if (!blob) {
        toastError('Failed to process image. Please try again.');
        setCropping(false);
        return;
      }

      const file = new File([blob], fileName, { type: mimeType });
      onCropComplete(file, cropBox(cropper));
      onClose();
      setCropping(false);
    } catch {
      toastError('Failed to crop image. Please try again.');
      if (mountedRef.current) setCropping(false);
    }
  };

  const handleClose = () => {
    setImageDataUrl(null);
    setError(null);
    onClose();
  };

  const handleReplaceClick = () => {
    fileInputRef.current?.click();
  };

  const handleRetry = () => {
    setReloadKey((key) => key + 1);
  };

  return (
    <Popup
      open={open}
      contentStyle={popupStyle}
      closeOnDocumentClick={false}
      className="popup-modal"
      onClose={handleClose}
    >
      <div className="popup-modal-container">
        <div className="row m-0" style={{ borderBottom: '1px solid var(--sl-border-secondary)' }}>
          <div className="col d-flex" style={{ padding: '17px 24px' }}>
            <h3 className="mb-0 align-self-center" style={{ fontSize: '20px', color: 'var(--sl-text-primary)' }}>
              <strong>
                <i className="far fa-crop text-primary mr-2"></i>
                Crop Image
              </strong>
            </h3>
          </div>
          <div className="col-xs-auto d-flex align-items-center" style={{ paddingRight: '24px' }}>
            <a
              href="#"
              style={{ fontSize: '20px', color: 'var(--sl-text-primary)' }}
              onClick={(e) => {
                e.preventDefault();
                handleClose();
              }}
            >
              <i className="fal fa-times"></i>
            </a>
          </div>
        </div>

        <div className="row m-0">
          <div className="col-12" style={{ padding: '18px 24px 24px 24px' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            {loading ? (
              <div style={{
                height: '400px',
                backgroundColor: 'var(--sl-bg-tertiary)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div className="text-center text-muted">
                  <i className="fas fa-spinner fa-spin fa-2x mb-2 d-block"></i>
                  Loading image...
                </div>
              </div>
            ) : error === 'load' ? (
              <div style={{
                height: '400px',
                backgroundColor: 'var(--sl-bg-tertiary)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div className="text-center" style={{ padding: '20px' }}>
                  <i className="fas fa-image fa-3x mb-3 d-block" style={{ color: 'var(--sl-text-disabled)' }}></i>
                  <p className="mb-3" style={{ color: 'var(--sl-text-tertiary)' }}>
                    Couldn&apos;t load this image. Please try again.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary mr-2"
                    onClick={handleRetry}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="fas fa-redo mr-2"></i>
                    Retry
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleReplaceClick}
                    style={{ borderRadius: '8px' }}
                  >
                    <i className="fas fa-upload mr-2"></i>
                    Choose a different image
                  </button>
                </div>
              </div>
            ) : loadedImageSrc ? (
              <div style={{
                height: '400px',
                backgroundColor: 'var(--sl-bg-primary-solid)',
                borderRadius: '4px'
              }}>
                <Cropper
                  ref={cropperRef}
                  src={loadedImageSrc}
                  {...(initialCoordinates ? { defaultCoordinates: initialCoordinates } : {})}
                  stencilProps={{
                    aspectRatio: aspectRatio,
                    grid: true
                  }}
                  style={{
                    height: '100%',
                    width: '100%'
                  }}
                />
              </div>
            ) : (
              <div style={{
                height: '400px',
                backgroundColor: 'var(--sl-bg-tertiary)',
                borderRadius: '4px'
              }} />
            )}

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                style={{ borderRadius: '8px', fontSize: '14px' }}
                className="btn btn-outline-secondary"
                onClick={handleClose}
                disabled={cropping}
              >
                <strong>Cancel</strong>
              </button>
              <button
                type="button"
                style={{ borderRadius: '8px', fontSize: '14px' }}
                className="btn btn-primary"
                onClick={handleCropAndUpload}
                disabled={cropping || loading || !loadedImageSrc || !!error}
              >
                {cropping ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Processing...
                  </>
                ) : (
                  <strong>Crop &amp; Upload</strong>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

ImageCropModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCropComplete: PropTypes.func.isRequired,
  imageSrc: PropTypes.string,
  aspectRatio: PropTypes.number.isRequired,
  fileName: PropTypes.string.isRequired,
  initialCrop: PropTypes.shape({
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }),
  onImageReplaced: PropTypes.func
};

export default ImageCropModal;
